/**
 * OAuthButtonField -- Dynamic field for initiating OAuth 2.0 authorization.
 *
 * Reads `oauth_config` from the field schema and integrates with the
 * `useOAuthPopup` hook (which handles PKCE, cryptographic state, popup
 * lifecycle, and completion exchange).
 *
 * States: idle, loading/connecting, connected (green indicator), error.
 * Supports disconnect to clear stored credentials.
 *
 * Accessibility: ARIA labels, describedby for help/error text, role="alert"
 * for errors, live region for status announcements.
 */

import { useEffect, useRef, useState } from 'react';
import { Loader2, CheckCircle2, ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOAuthPopup } from '@/hooks/useOAuthPopup';
import { LiveRegion } from '../LiveRegion';
import type { DynamicFieldProps } from './index';
import type { OAuthConfig } from '@/types/connector-schema';

export function OAuthButtonField({ field, value, onChange, onBatchFieldChange, error, readOnly }: DynamicFieldProps) {
  const fieldId = `field-${field.name}`;
  const helpId = `${fieldId}-help`;
  const errorId = `${fieldId}-error`;
  const statusId = `${fieldId}-status`;

  // Read oauth_config from the field definition
  const oauthConfig = field.oauth_config as OAuthConfig | undefined;
  const provider = oauthConfig?.provider ?? 'unknown';
  const providerLabel = provider
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

  // Screen reader announcements for OAuth state changes
  const [oauthAnnouncement, setOauthAnnouncement] = useState('');

  // Use the OAuth popup hook -- pass full config so endpoints are provider-specific
  const {
    startOAuth,
    isLoading,
    error: oauthError,
    isConnected: oauthConnected,
    result,
    reset,
  } = useOAuthPopup(oauthConfig ?? provider);

  // Announce OAuth state changes to screen readers. Adjust state during
  // render instead of in an effect
  // (https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes).
  // The 4-value dependency array is mirrored as one serialized key (same
  // `JSON.stringify([...])` composite-key idiom OAuthPopulatedDropdownField.tsx
  // uses for its multi-value `depsSignature`), so the announcement updates
  // only when any of the four actually changes -- the guard converges on the
  // very next render once the mirror catches up.
  const announceDepsKey = JSON.stringify([isLoading, oauthConnected, oauthError, providerLabel]);
  const [prevAnnounceDepsKey, setPrevAnnounceDepsKey] = useState(announceDepsKey);
  if (announceDepsKey !== prevAnnounceDepsKey) {
    setPrevAnnounceDepsKey(announceDepsKey);
    if (isLoading) {
      setOauthAnnouncement(`Connecting to ${providerLabel}`);
    } else if (oauthConnected) {
      setOauthAnnouncement('Successfully connected');
    } else if (oauthError) {
      setOauthAnnouncement('Connection failed');
    }
  }

  // Determine connected state: either the hook says connected, or we already
  // have a value (e.g. editing an existing connector that is already authorized).
  const hasExistingValue = !!value;
  const isConnected = hasExistingValue || oauthConnected;

  // Track whether we have already notified the parent for the current result
  // to prevent re-triggering onChange in an infinite loop.
  const notifiedResultRef = useRef<string | null>(null);

  // When the hook reports a successful OAuth result, push credentials to the
  // form via onChange.
  useEffect(() => {
    if (result?.success && result.credentials) {
      const resultId = result.portal_id ?? result.session_token ?? 'success';
      if (notifiedResultRef.current !== resultId) {
        notifiedResultRef.current = resultId;
        // Use batch update to set both the OAuth credentials and any sibling
        // fields (e.g. portal_id) atomically, avoiding stale-closure races.
        if (onBatchFieldChange) {
          const updates: Record<string, unknown> = {
            [field.name]: result.credentials,
          };
          if (result.portal_id) {
            updates.portal_id = result.portal_id;
          }
          onBatchFieldChange(updates);
        } else {
          onChange(result.credentials);
        }
      }
    }
  }, [result, onChange, onBatchFieldChange, field.name]);

  const handleConnect = async () => {
    if (isConnected) {
      // Allow re-authorization: reset the hook and the form value first
      reset();
      notifiedResultRef.current = null;
    }
    await startOAuth();
  };

  const handleDisconnect = () => {
    reset();
    notifiedResultRef.current = null;
    onChange(null);
  };

  // Combine field-level validation error with hook-level OAuth error
  const displayError = error ?? oauthError ?? null;

  const describedBy = [
    field.help_text ? helpId : null,
    displayError ? errorId : null,
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  return (
    <div className="space-y-2">
      {/* Label row */}
      <div className="flex items-center gap-1">
        <Label htmlFor={fieldId}>
          {field.label}
          {field.required && (
            <>
              <span aria-hidden="true" className="text-destructive ml-1">*</span>
              <span className="sr-only">(required)</span>
            </>
          )}
        </Label>

        {field.tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle
                  className="h-3.5 w-3.5 text-muted-foreground cursor-help"
                  aria-hidden="true"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-sm">{field.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* OAuth button + status */}
      {isConnected && !isLoading ? (
        <div className="space-y-2">
          {/* Connected indicator */}
          <div
            id={statusId}
            className="flex items-center gap-2"
            role="status"
            aria-live="polite"
          >
            <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />
            <span className="text-sm text-success">
              Connected to {providerLabel}
              {result?.portal_id ? ` (Portal ${result.portal_id})` : ''}
            </span>
          </div>

          {/* Disconnect + Re-authorize controls */}
          {!readOnly && (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleConnect}
                aria-label={`Re-authorize with ${providerLabel}`}
              >
                Re-authorize
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDisconnect}
                aria-label={`Disconnect from ${providerLabel}`}
              >
                Disconnect
              </Button>
            </div>
          )}
        </div>
      ) : (
        <Button
          id={fieldId}
          type="button"
          variant="outline"
          onClick={handleConnect}
          disabled={readOnly || isLoading}
          aria-label={`Connect with ${providerLabel}`}
          aria-describedby={describedBy}
          className={cn('w-full h-11', isLoading && 'opacity-80')}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
              Connecting to {providerLabel}...
            </>
          ) : (
            <>
              <ExternalLink className="h-4 w-4 mr-2" aria-hidden="true" />
              Connect with {providerLabel}
            </>
          )}
        </Button>
      )}

      {/* Help text */}
      {field.help_text && (
        <p id={helpId} className="text-sm text-muted-foreground">
          {field.help_text}
        </p>
      )}

      {/* Error display */}
      {displayError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <span id={errorId} role="alert">{displayError}</span>
          </AlertDescription>
        </Alert>
      )}

      <LiveRegion message={oauthAnnouncement} priority="assertive" />
    </div>
  );
}
